/**
 * Moodscape candle personalization.
 *
 * PDP shows a "Personalize your candle" button. Clicking it opens a modal with
 * a live label preview and the fields the Worker's /config declares: one name
 * field on the greeting designs, a message plus a name on the custom design.
 *
 * Activates only when /config responds with mode:"candle", so wall art
 * continues through ms-personalization.js untouched.
 *
 * Requires window.MS_PERSONALIZATION_WORKER_URL set in layout/theme.liquid.
 */
(function () {
  "use strict";

  var WORKER = window.MS_PERSONALIZATION_WORKER_URL;
  if (!WORKER) {
    console.warn("[ms-candle] MS_PERSONALIZATION_WORKER_URL not set");
    return;
  }

  var LANGS = [
    { code: "en", label: "English" },
    { code: "te", label: "Telugu" },
    { code: "ta", label: "Tamil" },
    { code: "hi", label: "Hindi" },
    { code: "kn", label: "Kannada" },
    { code: "ml", label: "Malayalam" },
    { code: "gu", label: "Gujarati" },
    { code: "pa", label: "Punjabi" },
    { code: "bn", label: "Bengali" }
  ];

  var DEBOUNCE = 350;

  var root = document.querySelector("[data-ms-candle-root]");
  if (!root) return;

  var cfg = null;
  var saved = null;
  var lastFocus = null;
  var state = {};   // key -> { input, languageCode, rendered }
  var timers = {};

  function el(sel) { return root.querySelector(sel); }

  /**
   * Belt and braces alongside the Liquid rule: hide any variant-picker row for
   * the hidden "Personalization ID" option on themes that don't emit
   * data-option-position.
   */
  function hideOptionRow() {
    var labels = document.querySelectorAll(
      ".pdp-option-group, .product-form__input, .variant-label, legend, label"
    );
    Array.prototype.forEach.call(labels, function (node) {
      var text = (node.textContent || "").trim().toLowerCase();
      if (text.indexOf("personalization id") !== 0) return;
      var row = node.closest(".pdp-option-group, .product-form__input, fieldset");
      if (row) row.style.display = "none";
    });
  }

  function currentVariantId() {
    var input = document.querySelector('form[action*="/cart/add"] [name="id"]');
    return input ? input.value : null;
  }

  function storeKey() { return "ms-candle:" + currentVariantId(); }

  function persistSaved() {
    try { sessionStorage.setItem(storeKey(), JSON.stringify(saved)); } catch (e) {}
  }

  function clearPersisted() {
    try { sessionStorage.removeItem(storeKey()); } catch (e) {}
  }

  /** Flip the PDP button and status line to their saved state. */
  function setSavedUi(on) {
    var open = el("[data-ms-candle-open]");
    var status = el("[data-ms-candle-status]");
    var btn = el("[data-ms-candle-save]");
    if (on) {
      open.textContent = "Edit personalization";
      status.textContent = "Design saved. Add to cart when you are ready.";
      status.hidden = false;
      if (btn) { btn.textContent = "Saved"; btn.disabled = true; }
      root.classList.add("is-saved");
    } else {
      open.textContent = "Personalize your candle";
      status.hidden = true;
      if (btn) { btn.textContent = "Save design"; btn.disabled = false; }
      root.classList.remove("is-saved");
    }
  }

  /**
   * A reload always starts a fresh session: never bring a saved design back.
   * If this tab left one behind, forget it and ask the Worker to retire the
   * ghost so its picture is removed. The Worker refuses to retire anything
   * already in the cart or ordered, so a design in the cart is never lost.
   */
  function discardLeftover() {
    var raw = null;
    try {
      Object.keys(sessionStorage).forEach(function (k) {
        if (k.indexOf("ms-candle:") !== 0) return;
        raw = sessionStorage.getItem(k);
        sessionStorage.removeItem(k);
        var s = null;
        try { s = JSON.parse(raw); } catch (e) {}
        if (s && s.personalizationId) {
          fetch(WORKER + "/api/personalization/retire", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ personalizationId: s.personalizationId })
          }).catch(function () { /* cleanup cron catches anything missed */ });
        }
      });
    } catch (e) { /* storage blocked: nothing was saved, nothing to discard */ }
  }

  var CHANGE_WARNING = root.dataset.changeWarning ||
    "Changing this will remove your saved design. You will need to personalize again.";

  /** Promise-based yes/no, resolved by the two buttons in the confirm overlay. */
  function askConfirm(message) {
    return new Promise(function (resolve) {
      var overlay = el("[data-ms-candle-confirm]");
      var textEl = el("[data-ms-candle-confirm-text]");
      var yes = el("[data-ms-candle-confirm-yes]");
      var no = el("[data-ms-candle-confirm-no]");
      if (!overlay || !yes || !no) { resolve(true); return; }

      textEl.textContent = message;
      overlay.hidden = false;

      function done(v) {
        overlay.hidden = true;
        yes.removeEventListener("click", onYes);
        no.removeEventListener("click", onNo);
        resolve(v);
      }
      function onYes() { done(true); }
      function onNo() { done(false); }
      yes.addEventListener("click", onYes);
      no.addEventListener("click", onNo);
    });
  }

  /**
   * Discard the saved design locally and tell the Worker to retire the ghost,
   * so an abandoned variant does not linger in anyone's gallery.
   */
  function retireSaved() {
    if (!saved) return;
    var id = saved.personalizationId;
    saved = null;
    clearPersisted();
    fetch(WORKER + "/api/personalization/retire", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ personalizationId: id })
    }).catch(function () { /* cleanup cron catches anything missed */ });
  }

  function restoreProductImage() { removeInjected(); }

  /** Put the scent picker back when the customer declines the warning. */
  function revertVariantSelection(variantId) {
    var input = document.querySelector('form[action*="/cart/add"] [name="id"]');
    if (input) {
      input.value = variantId;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    }
    try {
      var url = new URL(window.location.href);
      url.searchParams.set("variant", variantId);
      history.replaceState({}, "", url.toString());
    } catch (e) {}
    if (saved) swapProductImage(saved.previewImageUrl);
  }

  /** Reopening the editor with a design already saved discards it first. */
  function guardedOpen() {
    if (!saved) { openModal(); return; }
    askConfirm(CHANGE_WARNING).then(function (ok) {
      if (!ok) return;
      retireSaved();
      setSavedUi(false);
      restoreProductImage();
      cfg.fields.forEach(function (f) {
        state[f.key] = { input: "", languageCode: "en", rendered: "" };
      });
      buildFields();
      paintPreview();
      openModal();
    });
  }

  /**
   * Switching scent after saving means the ghost no longer matches the chosen
   * variant, so the design has to be discarded or the selection reverted.
   */
  function watchVariantChanges() {
    document.addEventListener("change", function (e) {
      if (!saved) return;
      var inForm = e.target.closest && e.target.closest(
        'form[action*="/cart/add"], variant-selects, variant-radios, .product-form__input, .pdp-option-group'
      );
      if (!inForm) return;

      setTimeout(function () {
        var current = currentVariantId();
        if (!saved || !current || String(current) === String(saved.baseVariantId)) return;
        askConfirm(CHANGE_WARNING).then(function (ok) {
          if (ok) {
            retireSaved();
            setSavedUi(false);
            restoreProductImage();
            cfg.fields.forEach(function (f) {
              state[f.key] = { input: "", languageCode: "en", rendered: "" };
            });
            buildFields();
            paintPreview();
            openModal();
          } else {
            revertVariantSelection(saved.baseVariantId);
          }
        });
      }, 50);
    }, true);
  }

  function langLabel(code) {
    var m = LANGS.filter(function (l) { return l.code === code; })[0];
    return m ? m.label : code;
  }

  /* ---------- modal ---------- */

  function openModal() {
    lastFocus = document.activeElement;
    el("[data-ms-candle-modal]").hidden = false;
    document.body.style.overflow = "hidden";
    // The stage only has a measurable width once the modal is visible.
    paintPreview();
    var first = root.querySelector(".ms-candle__input");
    if (first) first.focus();
    document.addEventListener("keydown", onKeydown);
  }

  function closeModal() {
    el("[data-ms-candle-modal]").hidden = true;
    document.body.style.overflow = "";
    document.removeEventListener("keydown", onKeydown);
    if (lastFocus) lastFocus.focus();
  }

  function onKeydown(e) { if (e.key === "Escape") closeModal(); }

  /**
   * While the modal is open, Enter must never reach the product form. Bound on
   * the modal in capture phase so it fires before the form's submit handler.
   */
  function blockEnterSubmit(e) {
    if (e.key !== "Enter") return;
    if (e.target && e.target.tagName === "BUTTON") return;
    e.preventDefault();
    e.stopPropagation();
  }

  /* ---------- transliteration ---------- */

  function transliterate(key) {
    var v = state[key];
    var text = v.input.trim();

    if (!text || v.languageCode === "en") {
      v.rendered = text;
      paintPreview();
      return;
    }

    fetch(WORKER + "/api/personalization/transliterate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: text, languageCode: v.languageCode })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        v.rendered = data && data.rendered ? data.rendered : text;
        paintPreview();
      })
      .catch(function () { v.rendered = text; paintPreview(); });
  }

  function schedule(key) {
    clearTimeout(timers[key]);
    timers[key] = setTimeout(function () { transliterate(key); }, DEBOUNCE);
  }

  /* ---------- preview ---------- */

  /**
   * Positions each line at the same percentage of height the Worker renders it,
   * over the same background image, so the preview matches the print file.
   */
  function paintPreview() {
    var stage = el("[data-ms-candle-preview]");
    if (!stage || !cfg) return;

    stage.style.setProperty("--msc-ratio", (cfg.height / cfg.width) * 100 + "%");
    stage.style.backgroundImage = "url(" + cfg.backgroundUrl + ")";

    var layer = el("[data-ms-candle-layer]");
    layer.innerHTML = "";

    // Stage width is 0 while the modal is hidden; fall back to the canvas size
    // so the first paint is still correct once it becomes visible.
    var stageWidth = stage.getBoundingClientRect().width || cfg.width;
    var scale = stageWidth / cfg.width;

    cfg.fields.forEach(function (f) {
      var v = state[f.key];
      if (!v || !v.rendered.trim()) return;

      var line = document.createElement("div");
      line.className = "ms-candle__line";
      if (v.languageCode !== "en") line.classList.add("is-indic");
      line.textContent = v.rendered;
      // Same ink the Worker prints with (maroon on the diya labels).
      if (cfg.textColor) line.style.color = cfg.textColor;
      line.style.top = (f.centreY * 100) + "%";
      // Horizontal centre and width the Worker prints at. Labels without these
      // (the original four) fall back to the old centred, 80%-wide line.
      line.style.left = ((f.centreX != null ? f.centreX : 0.5) * 100) + "%";
      line.style.maxWidth = ((f.widthFrac || 0.8) * 100) + "%";
      // Scale the glyph by the stage's measured width against the canvas width,
      // so the preview tracks the print file at any viewport size. Computed in
      // px rather than container units: the stage gets its height from
      // percentage padding, so a size container would resolve to zero here.
      line.style.fontSize = (f.glyphHeight * scale) + "px";
      layer.appendChild(line);
    });

    markDirty();
  }

  function markDirty() {
    if (!saved) return;
    saved = null;
    clearPersisted();
    setSavedUi(false);
  }

  /* ---------- save ---------- */

  function validate() {
    var warn = el("[data-ms-candle-error]");
    for (var i = 0; i < cfg.fields.length; i++) {
      var f = cfg.fields[i];
      if (f.required !== false && !state[f.key].input.trim()) {
        warn.textContent = f.label + " is required.";
        warn.hidden = false;
        return false;
      }
    }
    warn.hidden = true;
    return true;
  }

  function save() {
    if (!validate()) return;

    var btn = el("[data-ms-candle-save]");
    btn.disabled = true;
    root.classList.add("is-saving");

    fetch(WORKER + "/api/personalization/create", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        baseVariantId: currentVariantId(),
        productHandle: root.getAttribute("data-product-handle"),
        values: cfg.fields.map(function (f) {
          return {
            key: f.key,
            input: state[f.key].input,
            languageCode: state[f.key].languageCode
          };
        }),
        clientNonce: String(Date.now()) + Math.random().toString(16).slice(2)
      })
    })
      .then(function (r) {
        return r.json().then(function (body) {
          if (!r.ok) throw new Error(body.message || body.error || "save_failed");
          return body;
        });
      })
      .then(function (data) {
        saved = data;
        // Which scent this ghost belongs to, so a scent change can be detected.
        saved.baseVariantId = String(currentVariantId());
        // Keep the typed values too, so a reload can repopulate the fields.
        saved.values = cfg.fields.map(function (f) {
          return {
            key: f.key,
            input: state[f.key].input,
            languageCode: state[f.key].languageCode,
            rendered: state[f.key].rendered
          };
        });
        persistSaved();
        swapProductImage(data.previewImageUrl);
        root.classList.remove("is-saving");
        setSavedUi(true);
        closeModal();
      })
      .catch(function (err) {
        var warn = el("[data-ms-candle-error]");
        warn.textContent = err.message || "Could not save. Please try again.";
        warn.hidden = false;
        btn.disabled = false;
        btn.textContent = "Save design";
        root.classList.remove("is-saving");
      });
  }

  /* ---------- gallery ---------- */

  /**
   * Normalise a Shopify CDN URL to a comparable key: same media, different size
   * or crop suffix, must match. Ported from the wall-art block.
   */
  function mediaKey(url) {
    if (!url) return "";
    var u = String(url).split("?")[0];
    u = u.substring(u.lastIndexOf("/") + 1);
    return u.replace(/_\d+x\d*(_crop_[a-z]+)?(@\dx)?(?=\.)/i, "");
  }

  /**
   * Other customers' saved labels, emitted by the snippet. Any variant whose
   * hidden personalization option is not "base" is someone else's ghost.
   */
  var hideKeys = {};
  try {
    JSON.parse(root.dataset.hideMedia || "[]").forEach(function (u) {
      var k = mediaKey(u);
      if (k) hideKeys[k] = 1;
    });
  } catch (e) { /* malformed list: hide nothing rather than break the page */ }

  function hideGhostMedia() {
    if (!Object.keys(hideKeys).length) return;
    // Never hide THIS customer's own design, which is injected, not hidden.
    var skip = (saved && saved.previewImageUrl) ? mediaKey(saved.previewImageUrl) : "";

    function maybeHide(node, src) {
      if (!node || (node.getAttribute && node.getAttribute("data-ms-candle-injected"))) return;
      var k = mediaKey(src);
      if (k && hideKeys[k] && k !== skip) node.style.display = "none";
    }

    document.querySelectorAll("[data-pdp-thumbs] .pdp-thumb").forEach(function (thumb) {
      var img = thumb.querySelector("img");
      maybeHide(thumb, thumb.getAttribute("data-full-src") || (img && img.getAttribute("src")) || "");
    });
    document.querySelectorAll(".pdp-stacked-images img").forEach(function (im) {
      maybeHide(im.closest(".pdp-stacked-image-wrap") || im, im.getAttribute("src") || "");
    });
    document.querySelectorAll(".product__media img, .product-media img, .product-single__media img").forEach(function (im) {
      maybeHide(im.closest("li, .product__media-item, [data-media-id]") || im, im.getAttribute("src") || "");
    });
  }

  var originalHero = null;

  function removeInjected() {
    document.querySelectorAll("[data-ms-candle-injected]").forEach(function (n) { n.remove(); });
    if (originalHero && originalHero.el) {
      originalHero.el.setAttribute("src", originalHero.src || "");
      if (originalHero.srcset) originalHero.el.setAttribute("srcset", originalHero.srcset);
    }
  }

  /**
   * Show the customer their own saved label immediately, without a refresh.
   * Handles all three gallery layouts this theme uses: the featured hero, the
   * stacked column, and the thumbnail rail. Ported from the wall-art block,
   * which is why the selectors look exhaustive.
   */
  function swapProductImage(url) {
    if (!url) return;
    removeInjected();

    var mainImg = document.querySelector(
      ".pdp-main-image__img, .product__media img, .product-media img, .product-single__media img"
    );
    if (mainImg) {
      if (originalHero === null) {
        originalHero = {
          el: mainImg,
          src: mainImg.getAttribute("src"),
          srcset: mainImg.getAttribute("srcset")
        };
      }
      mainImg.setAttribute("src", url);
      mainImg.removeAttribute("srcset");
    }

    var stacked = document.querySelector(".pdp-stacked-images");
    if (stacked) {
      var wrap = document.createElement("div");
      wrap.className = "pdp-stacked-image-wrap ms-candle-injected";
      wrap.setAttribute("data-ms-candle-injected", "1");
      var im = document.createElement("img");
      im.src = url;
      im.alt = "Your personalized label";
      im.style.width = "100%";
      im.style.height = "auto";
      wrap.appendChild(im);
      stacked.insertBefore(wrap, stacked.firstChild);
    }

    document.querySelectorAll("[data-pdp-thumbs]").forEach(function (rail) {
      rail.querySelectorAll(".pdp-thumb.is-active").forEach(function (x) {
        x.classList.remove("is-active");
      });
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "pdp-thumb is-active ms-candle-injected";
      btn.setAttribute("data-ms-candle-injected", "1");
      btn.setAttribute("data-full-src", url);
      btn.setAttribute("aria-label", "Your personalized label");
      var ti = document.createElement("img");
      ti.src = url;
      ti.alt = "Your personalized label";
      btn.appendChild(ti);
      btn.addEventListener("click", function () {
        if (mainImg) {
          mainImg.setAttribute("src", url);
          mainImg.removeAttribute("srcset");
        }
        rail.querySelectorAll(".pdp-thumb.is-active").forEach(function (x) {
          x.classList.remove("is-active");
        });
        btn.classList.add("is-active");
      });
      rail.insertBefore(btn, rail.firstChild);
    });

    // Re-run now that this customer's own design is injected: its key becomes
    // the skip, so it stays visible while everyone else's stays hidden.
    hideGhostMedia();
  }

  /* ---------- add to cart ---------- */

  /**
   * Swap the base variant for the saved ghost variant and attach the text as
   * customer-visible properties. The internal id stays out of the cart.
   */
  function interceptAddToCart() {
    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    form.addEventListener("submit", function (e) {
      if (!saved) return;
      e.preventDefault();

      var props = {};
      cfg.fields.forEach(function (f) {
        var v = state[f.key];
        if (!v.rendered.trim()) return;
        props[f.label] = v.rendered;
        if (v.languageCode !== "en") {
          props[f.label + " language"] = langLabel(v.languageCode);
        }
      });

      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [{ id: saved.ghostVariantId, quantity: 1, properties: props }]
        })
      })
        .then(function () {
          return fetch(
            WORKER + "/api/personalization/" +
            encodeURIComponent(saved.personalizationId) + "/mark-added",
            { method: "POST" }
          );
        })
        .then(function () { window.location.href = "/cart"; })
        .catch(function () { form.submit(); });
    });
  }

  /* ---------- build fields ---------- */

  function buildFields() {
    var wrap = el("[data-ms-candle-rows]");
    wrap.innerHTML = "";

    cfg.fields.forEach(function (f) {
      // A label can start its picker on a language (Telugu on the Telugu label).
      var startLang = f.transliterate && f.defaultLanguage ? f.defaultLanguage : "en";
      state[f.key] = { input: "", languageCode: startLang, rendered: "" };

      var row = document.createElement("div");
      row.className = "ms-candle__row";
      if (!f.transliterate) row.classList.add("is-plain");

      var label = document.createElement("label");
      label.className = "ms-candle__label";
      label.textContent = f.label;
      // Only labels that declare `required` get the hint, so the original four
      // look exactly as they do today.
      if (typeof f.required === "boolean") {
        var need = document.createElement("span");
        need.className = "ms-candle__need";
        need.textContent = (f.required ? " (required, up to " : " (optional, up to ") +
          f.maxChars + " characters)";
        label.appendChild(need);
      }

      var field = document.createElement("div");
      field.className = "ms-candle__field";

      var input = document.createElement("input");
      input.type = "text";
      input.maxLength = f.maxChars;
      input.className = "ms-candle__input";
      input.placeholder = f.transliterate ? "Type in English" : "Your message";
      input.setAttribute("aria-label", f.label);

      var count = document.createElement("span");
      count.className = "ms-candle__count";
      count.textContent = "0/" + f.maxChars;

      field.appendChild(input);
      field.appendChild(count);
      row.appendChild(label);
      row.appendChild(field);

      if (f.transliterate) {
        var select = document.createElement("select");
        select.className = "ms-candle__lang";
        select.setAttribute("aria-label", f.label + " language");
        LANGS.forEach(function (l) {
          var opt = document.createElement("option");
          opt.value = l.code;
          opt.textContent = l.label;
          select.appendChild(opt);
        });
        select.value = startLang;
        select.addEventListener("keydown", function (e) {
          if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); }
        });
        select.addEventListener("change", function () {
          state[f.key].languageCode = select.value;
          transliterate(f.key);
        });
        row.appendChild(select);
      }

      // The modal sits inside the product form, so a bare Enter here submits it
      // and adds the un-personalized base variant to cart. Swallow it.
      input.addEventListener("keydown", function (e) {
        if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); }
      });

      input.addEventListener("input", function () {
        state[f.key].input = input.value;
        count.textContent = input.value.length + "/" + f.maxChars;
        el("[data-ms-candle-error]").hidden = true;
        if (f.transliterate) {
          schedule(f.key);
        } else {
          state[f.key].rendered = input.value;
          paintPreview();
        }
      });

      wrap.appendChild(row);
    });
  }

  /* ---------- init ---------- */

  function init() {
    var variantId = currentVariantId();
    if (!variantId) return;

    fetch(WORKER + "/api/personalization/config?baseVariantId=" + encodeURIComponent(variantId))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || data.mode !== "candle") return;
        cfg = data;

        buildFields();
        paintPreview();
        interceptAddToCart();

        hideOptionRow();

        // Gallery hide on load, after late renders, and on any re-render.
        // Only toggles display, so the observer cannot loop.
        discardLeftover();
        watchVariantChanges();

        hideGhostMedia();
        setTimeout(hideGhostMedia, 300);
        setTimeout(hideGhostMedia, 1000);
        document.querySelectorAll(
          "[data-pdp-thumbs], .pdp-stacked-images, .product__media, .product-media, .product-single__media"
        ).forEach(function (node) {
          try { new MutationObserver(hideGhostMedia).observe(node, { childList: true, subtree: true }); }
          catch (e) { /* observer unsupported: the timed passes still cover it */ }
        });

        var open = el("[data-ms-candle-open]");
        open.hidden = false;
        open.addEventListener("click", guardedOpen);
        el("[data-ms-candle-save]").addEventListener("click", save);

        Array.prototype.forEach.call(
          root.querySelectorAll("[data-ms-candle-close]"),
          function (b) { b.addEventListener("click", closeModal); }
        );

        var modalNode = el("[data-ms-candle-modal]");
        modalNode.addEventListener("click", function (e) {
          if (e.target === e.currentTarget) closeModal();
        });
        modalNode.addEventListener("keydown", blockEnterSubmit, true);
      })
      .catch(function () { /* wall-art path handles its own errors */ });
  }

  window.addEventListener("resize", function () {
    clearTimeout(timers.__resize);
    timers.__resize = setTimeout(paintPreview, 150);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
